import { isCountryCode } from '@draughts/shared';

/**
 * Best-effort detection of the user's country at registration time, used only
 * to pre-fill an editable picker — the user can always correct it.
 *
 * Strategy: scan `navigator.languages` (and `navigator.language`) for a BCP-47
 * region subtag (e.g. `nl-NL` -> `NL`) and return the first valid ISO 3166-1
 * alpha-2 code found. Returns null when no region can be derived.
 */
export function detectCountry(): string | null {
  if (typeof navigator === 'undefined') return null;

  const locales: string[] = [];
  if (Array.isArray(navigator.languages)) locales.push(...navigator.languages);
  if (navigator.language) locales.push(navigator.language);

  for (const locale of locales) {
    const region = regionFromLocale(locale);
    if (region && isCountryCode(region)) return region.toUpperCase();
  }
  return null;
}

/**
 * Extracts the region subtag from a BCP-47 locale string. Prefers the platform
 * `Intl.Locale` parser, falling back to a simple split for older engines.
 */
function regionFromLocale(locale: string): string | null {
  try {
    const region = new Intl.Locale(locale).region;
    if (region) return region.toUpperCase();
  } catch {
    // ignore malformed locale
  }
  const parts = locale.split(/[-_]/);
  const candidate = parts.find((p) => /^[A-Za-z]{2}$/.test(p) && p === p.toUpperCase());
  return candidate ? candidate.toUpperCase() : null;
}

/**
 * Localized country name for a code, via `Intl.DisplayNames`. Falls back to the
 * raw code when the runtime can't resolve it.
 */
export function countryName(code: string, locale: string): string {
  const upper = code.toUpperCase();
  // Unknown codes fall back to the raw code rather than `Intl`'s "Unknown
  // Region" (e.g. it resolves the reserved "ZZ" to a localized placeholder).
  if (!isCountryCode(upper)) return upper;
  try {
    const dn = new Intl.DisplayNames([locale], { type: 'region' });
    return dn.of(upper) ?? upper;
  } catch {
    return upper;
  }
}
